/**
 * Currency Debug Utilities - Track down exchange rate inconsistencies
 */

import { sessionCurrencyService, getSessionCurrencyStats } from '@/services/SessionCurrencyService';
import { currencyService } from '@/services/CurrencyService';

// Production flag - disable debug logging in production
const ENABLE_CURRENCY_DEBUG = import.meta.env.MODE === 'development' || import.meta.env.VITE_DEBUG_CURRENCY === 'true';

export interface CurrencyDebugInfo {
  component: string;
  amount: number;
  fromCurrency: string;
  toCurrency: string;
  convertedAmount: number;
  exchangeRate: number;
  source: 'session_locked' | 'fresh' | 'cache' | 'unknown';
  timestamp: number;
  sessionId: string;
}

class CurrencyDebugTracker {
  private static instance: CurrencyDebugTracker;
  private conversions: CurrencyDebugInfo[] = [];

  static getInstance(): CurrencyDebugTracker {
    if (!CurrencyDebugTracker.instance) {
      CurrencyDebugTracker.instance = new CurrencyDebugTracker();
    }
    return CurrencyDebugTracker.instance;
  }

  /**
   * Track a currency conversion for debugging
   */
  trackConversion(info: CurrencyDebugInfo): void {
    this.conversions.push(info);
    
    if (ENABLE_CURRENCY_DEBUG) {
      
      
      
    }
    
    // Keep only last 50 conversions to prevent memory issues
    if (this.conversions.length > 50) {
      this.conversions = this.conversions.slice(-50);
    }
  }

  /**
   * Get all conversions for a specific amount to identify discrepancies
   */
  getConversionsForAmount(amount: number, tolerance = 0.01): CurrencyDebugInfo[] {
    return this.conversions.filter(conv => 
      Math.abs(conv.amount - amount) <= tolerance
    );
  }

  /**
   * Find rate discrepancies for the same currency pair
   */
  findRateDiscrepancies(fromCurrency: string, toCurrency: string): {
    conversions: CurrencyDebugInfo[];
    uniqueRates: number[];
    maxDiscrepancy: number;
    discrepancyPercentage: number;
  } {
    const pairConversions = this.conversions.filter(conv => 
      conv.fromCurrency === fromCurrency && conv.toCurrency === toCurrency
    );

    const uniqueRates = [...new Set(pairConversions.map(conv => conv.exchangeRate))];
    const maxRate = Math.max(...uniqueRates);
    const minRate = Math.min(...uniqueRates);
    const maxDiscrepancy = maxRate - minRate;
    const discrepancyPercentage = uniqueRates.length > 1 ? ((maxDiscrepancy / minRate) * 100) : 0;

    return {
      conversions: pairConversions,
      uniqueRates,
      maxDiscrepancy,
      discrepancyPercentage
    };
  }

  /**
   * Generate comprehensive debug report
   */
  generateDebugReport(): {
    sessionStats: ReturnType<typeof getSessionCurrencyStats>;
    totalConversions: number;
    uniqueCurrencyPairs: number;
    recentConversions: CurrencyDebugInfo[];
    discrepancies: Array<{
      pair: string;
      discrepancyPercentage: number;
      uniqueRates: number[];
      components: string[];
    }>;
  } {
    const sessionStats = getSessionCurrencyStats();
    
    // Group by currency pair
    const pairGroups = new Map<string, CurrencyDebugInfo[]>();
    this.conversions.forEach(conv => {
      const pairKey = `${conv.fromCurrency}_${conv.toCurrency}`;
      if (!pairGroups.has(pairKey)) {
        pairGroups.set(pairKey, []);
      }
      pairGroups.get(pairKey)!.push(conv);
    });

    // Find discrepancies
    const discrepancies: Array<{
      pair: string;
      discrepancyPercentage: number;
      uniqueRates: number[];
      components: string[];
    }> = [];

    pairGroups.forEach((conversions, pairKey) => {
      const uniqueRates = [...new Set(conversions.map(c => c.exchangeRate))];
      if (uniqueRates.length > 1) {
        const maxRate = Math.max(...uniqueRates);
        const minRate = Math.min(...uniqueRates);
        const discrepancyPercentage = ((maxRate - minRate) / minRate) * 100;
        
        if (discrepancyPercentage > 0.1) { // More than 0.1% discrepancy
          discrepancies.push({
            pair: pairKey,
            discrepancyPercentage,
            uniqueRates,
            components: [...new Set(conversions.map(c => c.component))]
          });
        }
      }
    });

    return {
      sessionStats,
      totalConversions: this.conversions.length,
      uniqueCurrencyPairs: pairGroups.size,
      recentConversions: this.conversions.slice(-10),
      discrepancies: discrepancies.sort((a, b) => b.discrepancyPercentage - a.discrepancyPercentage)
    };
  }

  /**
   * Clear all tracked conversions
   */
  clearTracking(): void {
    this.conversions = [];
    
  }
}

// Export singleton instance
export const currencyDebugTracker = CurrencyDebugTracker.getInstance();

/**
 * Helper function to track a conversion with component context
 */
export async function trackCurrencyConversion(
  component: string,
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  convertedAmount: number,
  exchangeRate: number,
  source: 'session_locked' | 'fresh' | 'cache' | 'unknown' = 'unknown'
): Promise<void> {
  const sessionStats = getSessionCurrencyStats();
  
  currencyDebugTracker.trackConversion({
    component,
    amount,
    fromCurrency,
    toCurrency,
    convertedAmount,
    exchangeRate,
    source,
    timestamp: Date.now(),
    sessionId: sessionStats.sessionId
  });
}

/**
 * Generate and log debug report
 */
export function generateCurrencyDebugReport(): void {
  const report = currencyDebugTracker.generateDebugReport();
  
  console.group(`[CURRENCY-DEBUG] 📋 COMPREHENSIVE REPORT`);
  
  
  
  if (report.discrepancies.length > 0) {
    console.group(`🚨 EXCHANGE RATE DISCREPANCIES (${report.discrepancies.length})`);
    report.discrepancies.forEach(disc => {
      console.log(`🚨 ${disc.pair}: ${disc.discrepancyPercentage.toFixed(2)}% discrepancy`);
      console.log(`🚨 - Rates: [${disc.uniqueRates.join(', ')}]`);
      console.log(`🚨 - Components: [${disc.components.join(', ')}]`);
    });
    console.groupEnd();
  } else {
    console.log(`✅ No significant exchange rate discrepancies found`);
  }
  
  console.group(`📈 Recent conversions (last 10)`);
  report.recentConversions.forEach(conv => {
    console.log(`${conv.component}: ${conv.amount} ${conv.fromCurrency} → ${conv.convertedAmount} ${conv.toCurrency} (rate: ${conv.exchangeRate}, ${conv.source})`);
  });
  console.groupEnd();
  
  console.groupEnd();
  
  return report;
}

/**
 * Find conversions that resulted in specific amounts (useful for debugging)
 */
export function findConversionsForAmount(targetAmount: number, tolerance = 1): CurrencyDebugInfo[] {
  return currencyDebugTracker.getConversionsForAmount(targetAmount, tolerance);
}

/**
 * Enhanced logging for specific amounts we're tracking
 */
export function logTargetAmount(amount: number, currency: string, component: string): void {
  
  // Check if this matches our target amounts
  const targets = [124027.63, 128186.87, 1414.71];
  const matchingTarget = targets.find(target => Math.abs(amount - target) < 1);
  
  if (matchingTarget) {
    
    // Find all conversions that resulted in this amount
    const matchingConversions = findConversionsForAmount(matchingTarget, 1);
    if (matchingConversions.length > 0) {
      
    }
  }
}